
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  FeedbackRepositoryInterface,
  FeedbackStatus,
  TicketFeedbackInterface,
} from '@/services/QC-V2/feedback/interfaces';
import {
  CommentCreateInterface,
  ProfileMapInterface,
} from '@/services/QC-V2/chat/interfaces';
import { Roles } from '@/services/Roles';
import { ScoreTypeInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface } from '@/services/QC-V2/score-grade/interfaces';
import { CreateTagInterface, TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import { CommentRate, CommentRepositoryInterface } from '@/services/QC-V2/comment/interfaces';
import {
  TicketReviewFormDataInterface,
  TicketReviewInterface,
} from '@/services/QC-V2/ticket/interfaces';
import DeleteCommentDialog from '@/components/dialogs/qc-v2/comment/DeleteCommentDialog.vue';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    DeleteCommentDialog,
  },
  inject: [
    'feedbackRepositoryV2',
    'tagRepositoryV2',
    'commentRepositoryV2',
  ],
})
export default class TicketFeedbackReview extends Vue {
  @Prop() private feedback!: TicketFeedbackInterface;
  @Prop() private profilesMap!: ProfileMapInterface;

  @Prop() private scoreTypesMap!: { [key: number]: string };
  @Prop() private scoreGradesMap!: { [key: number]: string };

  @Prop({ default: false }) private initiallyCollapsed!: boolean;

  @Prop() private scoreTypes!: ScoreTypeInterface[];
  @Prop() private scoreGrades!: ScoreGradeInterface[];
  @Prop() private tags!: TagInterface[];

  private tagsMap: { [key: number]: TagInterface } = {};

  private readonly feedbackRepositoryV2!: FeedbackRepositoryInterface;
  private readonly commentRepositoryV2!: CommentRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;
  private loading: boolean = true;
  private formValid: boolean = false;

  private userRoles: object = Roles;

  private collapsed: boolean = false;
  private showReviewEditSection: boolean = false;

  private searchTag: string = '';

  private commentRates: typeof CommentRate = CommentRate;
  private commentRate: CommentRate = CommentRate.NOT_RATED;
  private editCommentRate: CommentRate = CommentRate.NOT_RATED;
  private ticketComment: string = '';

  private commentIndexForEdit: number = -1;
  private commentIdForEdit: number = 0;
  private editCommentText: string = '';

  private deleteCommentDialogVisible: boolean = false;
  private deleteCommentDialogEvent!: string;
  private deleteCommentDialogData!: { reviewId?: number, reviewIndex?: number, commentId?: number };

  private reviews: TicketReviewInterface[] = [];

  private formData: TicketReviewFormDataInterface = {
    ticketId: this.feedback.ticket.id,
    reviewerId: this.$store.getters.id,
    reviewedId: this.feedback.feedbackTo,
    isDraft: false,
    scores: {},
    tags: [],
    comments: [],
  };

  private async created(): Promise<void> {
    await this.initialize();
    await this.createTagMap();
    this.loading = false;
  }

  @Watch('feedback')
  private async handleFeedbackChange(): Promise<void> {
    this.loading = true;
    await this.initialize();
    this.loading = false;
  }

  private async initialize(): Promise<void> {
    this.reviews = this.feedback.ticket.reviews.filter((r) => r.reviewedId === this.feedback.feedbackTo);
    const review = this.feedback.ticket.reviews.find((r) => !r.deletedAt && r.reviewedId === this.feedback.feedbackTo);
    if (!review) {
      this.$errorDialog.show('Feedback not found for given user!');
      return;
    }

    await this.changeFeedbackStatus();
    this.formData.reviewedId = this.feedback.feedbackTo;
  }

  private async editReview(): Promise<void> {
    if (this.ticketComment.trim() !== '') {
      this.$infoDialog.show('You have unsent comment!');
      return;
    }

    try {
      await this.feedbackRepositoryV2.updateTicketFeedback(this.feedback.id, this.formData);
      this.addReview();

      this.formData = {
        ticketId: this.feedback.ticket.id,
        reviewerId: this.$store.getters.id,
        reviewedId: this.feedback.feedbackTo,
        isDraft: false,
        scores: {},
        tags: [],
        comments: [],
      };

      this.showReviewEditSection = false;
      this.$successDialog.show('Review edited successfully');
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private async changeFeedbackStatus(closed: boolean = false): Promise<void> {
    const viewer = this.$store.getters.id;

    if (this.feedback.feedbackTo === viewer && this.feedback.status === FeedbackStatus.SENT) {
      await this.feedbackRepositoryV2.changeTicketFeedbackStatus(this.feedback.id, FeedbackStatus.SEEN);
      this.feedback.status = FeedbackStatus.SEEN;
      return;
    }

    if (closed && this.$store.getters.id === this.feedback.feedbackFrom) {
      await this.feedbackRepositoryV2.changeTicketFeedbackStatus(this.feedback.id, FeedbackStatus.CLOSED);
      this.feedback.status = FeedbackStatus.CLOSED;
      this.$successDialog.show('Feedback closed successfully!');
    }
  }

  private addScore(scoreTypeId: number, scoreGradeId: number): void {
    this.formData.scores[scoreTypeId] = scoreGradeId;
    this.validateForm();
  }

  private removeScore(scoreTypeId: number): void {
    delete this.formData.scores[scoreTypeId];
    this.validateForm();
  }

  private validateForm(): void {
    this.formValid = (Object.values(this.formData.scores).every((score) => !!score))
      && (Object.keys(this.formData.scores).length === this.scoreTypes.length)
      && (!!this.formData.reviewedId)
      && ((!!this.formData.comments.length && !!this.formData.tags.length)
        || (!this.formData.comments.length && !this.formData.tags.length));
  }

  private async createTag(): Promise<void> {
    if (!this.searchTag) {
      return;
    }

    const data = {
      label: this.searchTag,
      color: '#808080FF',
    } as CreateTagInterface;

    const newTag = await this.tagRepositoryV2.createTag(data);
    this.tags.push(newTag);
    this.formData.tags.push((newTag));
    this.formData.tags = this.formData.tags.filter((tag: TagInterface | string) => typeof tag !== 'string');
    this.tagsMap[newTag.id] = newTag;
  }

  private createTagMap(): void {
    this.tags.forEach((tag) => {
      this.tagsMap[tag.id] = tag;
    });
  }

  private showEditBox(commentId: number, commentText: string): void {
    if (this.commentIdForEdit === commentId) {
      this.editCommentText = '';
      this.commentIdForEdit = 0;
      return;
    }

    this.editCommentText = commentText;
    this.commentIdForEdit = commentId;
  }

  private async editReviewComment(
    reviewId: number,
    reviewIndex: number,
    commentId: number,
    commentIndex: number,
  ):
    Promise<void> {
    try {
      const commentData: CommentCreateInterface = {
        author: this.$store.getters.id,
        text: this.editCommentText,
        rate: this.commentRate,
        timestamp: new Date().toISOString(),
      };

      await this.commentRepositoryV2.updateTicketReviewComment(reviewId, commentId, commentData);
      this.$successDialog.show('Comment Edited Successfully!');
      this.reviews[reviewIndex].comments[commentIndex].text = this.editCommentText;

      this.commentIdForEdit = 0;
      this.editCommentText = '';
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  // private async deleteReviewComment(reviewId: number, reviewIndex: number, commentId: number): Promise<void> {
  //   try {
  //     await this.commentRepositoryV2.deleteTicketReviewComment(reviewId, commentId);
  //     this.$successDialog.show('Comment Deleted Successfully!');
  //     this.reviews[reviewIndex].comments = this.reviews[reviewIndex].comments
  //       .filter((comment) => comment.id !== commentId);
  //   } catch (e) {
  //     this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
  //   }
  // }

  private addReview(): void {
    this.formData.tags = this.formData.tags.map((tag) => ({ ...tag, tagId: tag.id }));
    this.feedback.ticket.reviews = this.feedback.ticket.reviews.sort((rA, rB) => {
      return new Date(rA.timestamp).getTime() - new Date(rB.timestamp).getTime();
    });

    this.feedback.ticket.reviews[this.feedback.ticket.reviews.length - 1].deletedAt = new Date();

    this.feedback.ticket.reviews.push({
      id: 0,
      reviewedId: this.formData.reviewedId,
      reviewerId: this.formData.reviewerId,
      comments: this.formData.comments.map((c) => ({
        id: 0,
        author: c.author,
        text: c.text,
        rate: c.rate,
        timestamp: c.timestamp,
      })),
      isDraft: this.formData.isDraft,
      scores: Object.keys(this.formData.scores).map((scoreTypeId) => ({
        id: 0,
        scoreTypeId: Number(scoreTypeId),
        scoreGradeId: this.formData.scores[Number(scoreTypeId)],
      })),
      deletedAt: undefined,
      tags: this.formData.tags.map((tag) => ({ tagId: tag.id })),
      timestamp: new Date(),
    });

    if (this.formData.tags.length) {
      this.formData.tags.forEach((tag) => {
        if (this.tagsMap[tag.id]) {
          return;
        }

        this.tagsMap[tag.id] = tag;
      });
    }

    this.commentIdForEdit = -1;
    this.commentIndexForEdit = -1;
  }

  private showEditBoxForNewReviewComment(index: number, commentText: string): void {
    if (this.commentIndexForEdit === index) {
      this.editCommentText = '';
      this.commentIndexForEdit = -1;

      return;
    }

    this.commentIndexForEdit = index;
    this.editCommentText = commentText;
  }

  private async createTicketComment(): Promise<void> {
    this.ticketComment = this.ticketComment.trim();
    if (!this.ticketComment) {
      return;
    }

    const commentData: CommentCreateInterface = {
      author: this.$store.getters.id,
      text: this.ticketComment,
      rate: this.commentRate,
      timestamp: new Date().toISOString(),
      addToDossier: true,
    };

    try {
      this.$successDialog.show('Comment Created Successfully!');
      this.formData.comments.push(commentData);
      this.ticketComment = '';
      this.commentRate = CommentRate.NOT_RATED;
      this.validateForm();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private showDeleteNewReviewCommentDialog(reviewIndex: number): void {
    this.deleteCommentDialogData = { reviewIndex };
    this.deleteCommentDialogEvent = 'review-comment-deleted';
    this.deleteCommentDialogVisible = true;
  }

  private showDeleteReviewCommentDialog(reviewId: number, reviewIndex: number, commentId: number): void {
    this.deleteCommentDialogData = { reviewId, reviewIndex, commentId };
    this.deleteCommentDialogEvent = 'review-comment-deleted';
    this.deleteCommentDialogVisible = true;
  }

  private async deleteReviewComment(data: { reviewId: number, reviewIndex: number, commentId: number }): Promise<void> {
    if (!data.commentId) {
      this.formData.comments = this.formData.comments.filter((_, index) => index !== data.reviewIndex);
      return;
    }

    try {
      await this.commentRepositoryV2.deleteTicketReviewComment(data.reviewId, data.commentId);
      this.$successDialog.show('Comment Deleted Successfully!');
      this.reviews[data.reviewIndex].comments = this.reviews[data.reviewIndex].comments
        .filter((comment) => comment.id !== data.commentId);
      this.deleteCommentDialogVisible = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private setCommentRate(rate: CommentRate): void {
    if (rate === this.commentRate) {
      this.commentRate = CommentRate.NOT_RATED;
      return;
    }

    this.commentRate = rate;
  }

  private setEditCommentRate(rate: CommentRate): void {
    if (rate === this.editCommentRate) {
      this.editCommentRate = CommentRate.NOT_RATED;
      return;
    }

    this.editCommentRate = rate;
  }

  private editNewReviewComment(index: number): void {
    this.formData.comments[index].text = this.editCommentText;
    this.formData.comments[index].rate = this.editCommentRate;
    this.commentIndexForEdit = -1;
  }

  private userHasScopes(scope: string): boolean {
    return this.$store.getters.scopes.includes(scope);
  }
}
